import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldCheck, AlertTriangle, Clock, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceService } from '@/services/compliance-service';
import type { AssetCompliance } from '@/types/compliance';
import { cn } from '@/lib/utils';

interface ComplianceSummary {
  total: number;
  compliant: number;
  dueSoon: number;
  overdue: number;
  nonApplicable: number;
  complianceRate: number;
}

export function ComplianceSummaryWidget() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComplianceSummary();
  }, []);

  const loadComplianceSummary = async () => {
    try {
      setLoading(true);
      const compliances = await complianceService.getAssetCompliances();
      
      // Filter out disposed/sold assets
      const activeCompliance = compliances.filter(
        (item: AssetCompliance) => 
          item.assetStatus !== 'Disposed' && item.assetStatus !== 'Sold'
      );

      let compliant = 0;
      let dueSoon = 0;
      let overdue = 0;
      let nonApplicable = 0;

      activeCompliance.forEach((item: AssetCompliance) => {
        if (item.complianceStatus === 'NonApplicable') {
          nonApplicable++;
          return;
        }

        if (!item.nextInspectionDate) {
          return;
        }

        const daysUntilDue = Math.ceil(
          (new Date(item.nextInspectionDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
        );

        if (daysUntilDue < 0) {
          overdue++;
        } else if (daysUntilDue <= 30) {
          dueSoon++;
        } else {
          compliant++;
        }
      });

      const total = activeCompliance.length;
      const complianceRate = total > 0 
        ? Math.round(((compliant + nonApplicable) / total) * 100) 
        : 100;

      setSummary({
        total,
        compliant,
        dueSoon,
        overdue,
        nonApplicable,
        complianceRate,
      });
    } catch (error) {
      console.error('Failed to load compliance summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCompliance = () => {
    navigate('/compliance/assets');
  };

  const handleViewOverdue = () => {
    navigate('/compliance/assets?filter=overdue');
  };

  const handleViewDueSoon = () => {
    navigate('/compliance/assets?filter=dueSoon');
  };

  if (loading) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Compliance Summary</CardTitle>
              <CardDescription>Overall compliance status</CardDescription>
            </div>
            <div className="p-2 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
              <ShieldCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!summary) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Compliance Summary</CardTitle>
              <CardDescription>Overall compliance status</CardDescription>
            </div>
            <div className="p-2 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
              <ShieldCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Unable to load compliance data</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getComplianceColor = (rate: number) => {
    if (rate >= 90) return 'text-emerald-600 dark:text-emerald-400';
    if (rate >= 70) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getComplianceBgColor = (rate: number) => {
    if (rate >= 90) return 'bg-emerald-50 dark:bg-emerald-950';
    if (rate >= 70) return 'bg-amber-50 dark:bg-amber-950';
    return 'bg-red-50 dark:bg-red-950';
  };

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col')}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Compliance Summary</CardTitle>
            <CardDescription>Overall compliance status</CardDescription>
          </div>
          <div className="p-2 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
            <ShieldCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        {/* Compliance Rate */}
        <div className={`flex items-center justify-between p-4 rounded-lg ${getComplianceBgColor(summary.complianceRate)}`}>
          <div>
            <p className="text-sm text-muted-foreground">Compliance Rate</p>
            <p className={`text-3xl font-bold ${getComplianceColor(summary.complianceRate)}`}>
              {summary.complianceRate}%
            </p>
          </div>
          <ShieldCheck className={`h-12 w-12 ${getComplianceColor(summary.complianceRate)}`} />
        </div>

        {/* Status Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Compliant */}
          <button
            type="button"
            className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer text-left"
            onClick={handleViewCompliance}
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <p className="text-xs font-medium text-muted-foreground">Compliant</p>
            </div>
            <p className="text-2xl font-bold">{summary.compliant}</p>
          </button>

          {/* Due Soon */}
          <button
            type="button"
            className="p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer text-left"
            onClick={handleViewDueSoon}
          >
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <p className="text-xs font-medium text-muted-foreground">Due Soon</p>
            </div>
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{summary.dueSoon}</p>
          </button>

          {/* Overdue */}
          <button
            type="button"
            className="p-3 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors cursor-pointer text-left"
            onClick={handleViewOverdue}
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <p className="text-xs font-medium text-muted-foreground">Overdue</p>
            </div>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{summary.overdue}</p>
          </button>

          {/* Non-Applicable */}
          <div className="p-3 rounded-lg border bg-card">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs font-medium text-muted-foreground">N/A</p>
            </div>
            <p className="text-2xl font-bold text-muted-foreground">{summary.nonApplicable}</p>
          </div>
        </div>

        {/* Total */}
        <div className="pt-3 border-t">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Requirements</span>
            <span className="font-semibold">{summary.total}</span>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewCompliance}
          className="w-full mt-2"
        >
          View All Compliance
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
